// @flow
import _ from 'lodash';
import { hasPermission } from 'utils/user-utils';
import { CURRENCY_CONDITION_VARIABLE } from 'omega-rtc/components/form/trident-campaigns/constants/campaign';
import i18n from 'i18n-js';

export function getProgressPercent(processedCount: number, totalCount: number) {
  if (totalCount <= 0) return totalCount;
  const progressPercent = (processedCount / totalCount) * 100;
  return Number.isInteger(progressPercent) ? progressPercent : progressPercent.toFixed(2);
}

// interim solution until we fetch usertype and events from UCM. TODO : https://jira.grab.com/browse/MARKET-992
export function getAllowedTargetUserTypesList(userPermissions: Object, targetTypeList: Array<Object>, actionType: string) {
  const resource = 'new_trident';
  const hasAllFieldsAccess = hasPermission(userPermissions, resource, '*', actionType);
  if (hasAllFieldsAccess) {
    return targetTypeList;
  }
  return targetTypeList.filter(({ value }) => hasPermission(userPermissions, resource, value, actionType));
}

export function getEventTypesList(listRuleTypes: Object, targetUserType: string) {
  let eventTypesObjectArray = _.get(listRuleTypes, targetUserType, []);
  eventTypesObjectArray = _.filter(eventTypesObjectArray, (eventTypeObject) => {
    const isInternalEventType = _.includes(_.toLower(eventTypeObject.group), 'internal');
    return !_.get(eventTypeObject, 'deprecated', false) && !isInternalEventType; // for deprecated event types, we don't allow user to create pages with its
  });
  const rootEventTypes = eventTypesObjectArray.filter(et => et.group === '');
  const eventGroups = _.groupBy(eventTypesObjectArray.filter(et => et.group !== ''), 'group');
  return { rootEventTypes, eventGroups };
}

export function getRuleTypesListForConditionNode(listRuleTypes: Object, model: Object, startingNode: string) {
  const targetUserType = _.get(model, 'targetUserType', '');
  let ruleTypes = [];
  let type = 'TREATMENTS.';
  _.map(_.get(model, `nodes.${startingNode}.data.scenarios`, []), (scenario) => {
    const eventType = _.get(scenario, 'eventType');
    const eventTypeData = _.find(_.get(listRuleTypes, targetUserType), { eventType });
    ruleTypes = _.uniqWith([...ruleTypes, ..._.get(eventTypeData, 'ruleTypes', [])], _.isEqual);
    // limit conditions are already shown in the limit panel, so we should hide it from rule builder
    ruleTypes = _.filter(ruleTypes, (ruleType) => {
      return _.get(ruleType, 'displayType', '') !== 'limit';
    });
    type = type.concat(_.get(scenario, 'eventType'));
  });
  const ruleTypesData = { ruleTypes, type };
  return ruleTypesData;
}

export function getListOfHedwigActions(listRuleTypes: Object) {
  let actionTypeList = [];
  let hedwigActions = [];
  _.map(listRuleTypes, targetTypeData => {
    targetTypeData.forEach((row) => {
      const actions = _.filter(_.get(row, 'allowedActions', []), ({ group, type }) => {
        return (group === 'comms (hedwig)' || group === 'bulk') && type !== 'bulkAwardReward';
      });
      actionTypeList = _.concat(actionTypeList, actions);
    });
  });
  actionTypeList.forEach(action => {
    hedwigActions = _.union(hedwigActions, [action.type]);
  });
  return hedwigActions;
}

export function getActionConfig(listRuleTypes: Object, actionType: string) {
  let result = null;

  _.some(listRuleTypes, targetTypeData => {
    return targetTypeData.some((row) => {
      const action = _.find(_.get(row, 'allowedActions', []), ({ type }) => {
        return type === actionType;
      });

      if (action) {
        result = action;
        return true;
      }

      return false;
    });
  });

  return result;
}

export function getStartingNodeOptions(listRuleTypes: Object, targetUserType: string) {
  const startingNodes = [];
  const eventTypesObjectArray = _.get(listRuleTypes, targetUserType, []);
  const hasTimeEvent = eventTypesObjectArray.some(event => event.eventType === 'onDateAndTime');
  const hasScenarioEvent = eventTypesObjectArray.some(event => event.eventType !== 'onDateAndTime');
  if (hasScenarioEvent) {
    startingNodes.push('scenarios');
  }
  if (hasTimeEvent) {
    startingNodes.push('time');
  }
  return startingNodes;
}

// getNodePopupContent returns pop-up text which contains node name, treatment, stats key info
export function getNodePopupContent(model: Object, pathToName: string, nodeKey: string, defaultName: string) {
  let content = _.get(model.nodes[nodeKey], pathToName, defaultName) || defaultName;

  const nodeType = _.get(model.nodes[nodeKey], 'type', '');
  const isRootNode = nodeType === 'scenarios' || nodeType === 'time';

  // add tm/tg mapping info
  if (!isRootNode) {
    const tmIDs = _.get(model, `nodeTreatmentMapping[${nodeKey}]`, []);
    if (tmIDs.length) {
      content += ` (tm: ${_.join(tmIDs, ',')})`;
    }
  } else {
    const tgID = _.get(model, `rootNodeTmGroupMapping[${nodeKey}]`, 0);
    if (tgID > 0) {
      content += ` (tg: ${tgID})`;
    }
  }

  return content;
}


export function getConditionsFlag(ruleTypes: Object) {
  const needCityID = _.find(ruleTypes, o => {
    return o.variableName === 'var.ride.pickupCityID';
  }) !== undefined;
  // whether we need service type ID selection box for this event type
  const needServiceTypeID = _.find(ruleTypes, o => {
    return o.variableName === 'var.ride.serviceID';
  }) !== undefined;
  const needCurrency = !_.isEmpty(_.find(ruleTypes, r => r.variableName === CURRENCY_CONDITION_VARIABLE));

  return { needCityID, needServiceTypeID, needCurrency };
}

export function getCityOptions(cities: Array, handleAllCitiesSelected: Function) {
  const allCitiesOption = {
    key: 0,
    value: 0,
    text: i18n.t('promotions_page.form.all_cities'),
    onClick: handleAllCitiesSelected,
  };
  const cityOptions = _.map(cities, city => ({
    key: city.cityID,
    value: city.cityID,
    text: city.name,
  }));
  return [allCitiesOption, ...cityOptions];
}


export function getTaxyTypeOptions(cities: Array, selectedPickupCities: Array, handleAllTaxiTypesSelected: Function) {
  const selectedCitiesObject = _.filter(cities, city => selectedPickupCities.indexOf(city.cityID) !== -1);
  const taxiTypes = _.flatten(_.map(selectedCitiesObject,
    city => _.map(city.taxiTypes,
      taxiType => ({ ...taxiType, cityCode: city.code }))));

  // code to filter duplicate taxi type ids
  const filteredTaxiTypes = taxiTypes.reduce((acc, current) => {
    const x = acc.find(item => item.taxiTypeID === current.taxiTypeID);
    if (!x) {
      return acc.concat([current]);
    }
    return acc;
  }, []);

  const taxiTypeIDs = _.sortBy(_.map(
    filteredTaxiTypes, taxiType => ({
      key: taxiType.taxiTypeID,
      value: taxiType.taxiTypeID,
      text: `${taxiType.cityCode} | ${taxiType.name}`,
    })
  ),
  ['text']);

  const allTaxiTypeIdsOption = {
    key: 0,
    value: 0,
    text: i18n.t('targets_page.fields.all_taxi_types'),
    onClick: () => handleAllTaxiTypesSelected(taxiTypeIDs),
  };
  return [allTaxiTypeIdsOption, ...taxiTypeIDs];
}

export function getActionsList(listRuleTypes, targetUserType, eventType) {
  // Filter only action that belong to the eventType
  const eventTypeData = _.find(_.get(listRuleTypes, targetUserType, []), { eventType });
  if (eventTypeData) {
    return _.filter(
      _.get(eventTypeData, 'allowedActions', []),
      a => {
        return a.group !== 'user stats';
      } // filter out hidden actions
    );
  }
  return [];
}


// Similar like getRuleTypesListForConditionNode above, but this one is only for single eventType
export function getRuleTypesForConditionNode(listRuleTypes: Object, targetUserType: string, eventType: string) { // TODO: Rename later
  const eventTypeData = _.find(_.get(listRuleTypes, targetUserType), { eventType });
  let ruleTypes = _.get(eventTypeData, 'ruleTypes', []);
  // limit conditions are already shown in the limit panel, so we should hide it from rule builder
  ruleTypes = _.filter(ruleTypes, (ruleType) => {
    return _.get(ruleType, 'displayType', '') !== 'limit';
  });
  const type = `TREATMENTS.${eventType}`;

  return { ruleTypes, type };
}
